import { useId } from 'react';
import { cn } from '../cn';
import { TextInput } from './TextInput';

export interface ColorFieldProps {
  value: string;
  onChange: (hex: string) => void;
  label?: string;
  className?: string;
}

const HEX = /^#[0-9a-fA-F]{6}$/;

/** Full colour picker: native colour input behind a swatch + hex text field. Commits only valid #rrggbb. */
export function ColorField({ value, onChange, label, className }: ColorFieldProps) {
  const id = useId();
  const safe = HEX.test(value) ? value : '#000000';
  return (
    <div className={cn('flex items-center gap-2', className)}>
      <label
        htmlFor={id}
        className="relative h-7 w-7 flex-none cursor-pointer overflow-hidden rounded border border-border2"
        style={{ background: safe }}
        aria-label={label ? `${label} colour` : 'Colour'}
      >
        <input
          id={id}
          type="color"
          value={safe}
          onChange={(e) => onChange(e.target.value.toLowerCase())}
          className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
        />
      </label>
      <TextInput
        variant="mono"
        compact
        value={value}
        onChange={(e) => {
          const v = e.target.value.trim();
          if (HEX.test(v)) onChange(v.toLowerCase());
          else if (/^#?[0-9a-fA-F]{0,6}$/.test(v)) onChange(v.startsWith('#') ? v : `#${v}`);
        }}
        className="w-[92px]"
        aria-label={label ? `${label} hex` : 'Colour hex'}
      />
    </div>
  );
}

export function hexToRgba(hex: string, alpha: number): string {
  const h = HEX.test(hex) ? hex.slice(1) : '000000';
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${Math.max(0, Math.min(1, alpha))})`;
}
